import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ApplicationRef,
  Component,
  createComponent,
  effect,
  ElementRef,
  EnvironmentInjector,
  inject,
  input,
  OnDestroy,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import * as Leaflet from 'leaflet';
import { BehaviorSubject, combineLatest, map, Observable, of } from 'rxjs';
import { Class } from 'src/app/shared/types';
import { ServiceStatus } from '../../lib/constants';
import { getStatusColor } from '../../lib/functions';
import { MapMarker } from '../../lib/types';
import {
  LeafletLegendComponent,
  LegendCategory,
} from './components/leaflet-legend/leaflet-legend.component';
import { LeafletPopupContentComponent } from './components/leaflet-popup-content/leaflet-popup-content.component';
import { LeafletTooltipContentComponent } from './components/leaflet-tooltip-content/leaflet-tooltip-content.component';
import { LeafletXContent } from './components/shared/leaflet-x-content.component';

/**
 * World Bounds limits the map to the world based on the latitude and longitude.
 */
const WORLD_BOUNDS = new Leaflet.LatLngBounds(
  new Leaflet.LatLng(-85.05112878, -180),
  new Leaflet.LatLng(85.05112878, 180),
);

@Component({
  selector: 'app-leaflet-map',
  imports: [CommonModule],
  templateUrl: './leaflet-map.component.html',
  styleUrls: ['./leaflet-map.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LeafletMapComponent implements AfterViewInit, OnDestroy {
  mapElement = viewChild<ElementRef<HTMLDivElement>>('map');

  readonly categories = input<{ label: string; count: number }[]>([]);

  readonly markers = input<MapMarker[]>();

  private readonly envInjector$: EnvironmentInjector =
    inject(EnvironmentInjector);

  private readonly appRef$: ApplicationRef = inject(ApplicationRef);

  private readonly router$: Router = inject(Router);

  public showingPopup$: Observable<boolean> = of(false);

  private legend: Leaflet.Control = new Leaflet.Control({
    position: 'bottomleft',
  });

  private resizeObserver!: ResizeObserver;

  private markerLayer: Leaflet.LayerGroup;

  private map: Leaflet.Map;

  private readonly paneColors = {
    yellow: 'markers-yellow-pane',
    green: 'markers-green-pane',
    red: 'markers-red-pane',
  } as const;

  constructor() {
    effect(() => {
      // React to changes in markers and categories signals
      this.markers();
      this.categories();

      if (this.map && this.markerLayer) {
        this.addMarkers();
        this.addLegend();
      }
    });
  }

  ngAfterViewInit(): void {
    this.initMap();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  private initMap(): void {
    const titleLayer = Leaflet.tileLayer(
      'http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&hl=en',
      {
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
        noWrap: true,
        maxZoom: 20,
      },
    );

    this.map = Leaflet.map('map').setView([37.0902, -95.7129], 4); // Centered on the USA with zoom level 4
    this.markerLayer = Leaflet.layerGroup();
    this.map.setMaxBounds(WORLD_BOUNDS);
    this.markerLayer.addTo(this.map);
    titleLayer.addTo(this.map);

    // Create panes to control z-index stacking of markers by status
    // Inline styles on panes override global CSS that sets pane z-index to 0
    this.map.createPane(this.paneColors.green).style.zIndex = '480';
    this.map.createPane(this.paneColors.yellow).style.zIndex = '490';
    this.map.createPane(this.paneColors.red).style.zIndex = '500';

    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => {
      this.map.invalidateSize();
    });

    const mapElement = this.mapElement();
    if (mapElement) {
      this.resizeObserver.observe(mapElement.nativeElement);
    }
  }

  private addMarkers(): void {
    if (!this.markerLayer) {
      return;
    }

    this.markerLayer.clearLayers();
    const openObservables: Observable<boolean>[] = [];
    const markers: Leaflet.CircleMarker[] = [];
    (this.markers() || []).forEach((item) => {
      const created = this.createLeafletMarker(item);
      openObservables.push(created.popupOpen);
      markers.push(created.marker);
    });

    this.markerLayer = Leaflet.layerGroup(markers).addTo(this.map);

    if (markers.length > 0) {
      const group = Leaflet.featureGroup(markers);
      this.map.fitBounds(group.getBounds(), { padding: [10, 10] });
    }

    this.showingPopup$ = combineLatest(openObservables).pipe(
      map((values) => values.some((value) => value)),
    );
  }

  private addLegend(): void {
    if (this.legend) {
      this.map.removeControl(this.legend);
    }

    const componentRef = createComponent(LeafletLegendComponent, {
      environmentInjector: this.envInjector$,
    });

    componentRef.setInput('categories', this.categories() as LegendCategory[]);
    this.appRef$.attachView(componentRef.hostView);

    this.legend = new Leaflet.Control({ position: 'bottomleft' });
    this.legend.onAdd = () => componentRef.location.nativeElement;

    this.legend.addTo(this.map);
  }

  private createLeafletMarker(item: MapMarker) {
    const status = item.getStatus();
    const marker = Leaflet.circleMarker([+item.latitude, +item.longitude], {
      fillColor: getStatusColor(item.getStatus()),
      color: 'black',
      stroke: true,
      fillOpacity: 0.75,
      opacity: 0.5,
      fill: true,
      radius: 10,
      weight: 1,
      pane: this.getPaneForStatus(status),
    });

    const popupOpen = new BehaviorSubject(false);

    const tooltipContent = this.createPopupComponent(item, true);
    marker.bindTooltip(tooltipContent);

    // Add click event to navigate to services page
    marker.on('click', () => {
      this.navigateToServices(item);
    });

    return { marker, popupOpen };
  }

  private getPaneForStatus(status?: string): string {
    switch (status) {
      case ServiceStatus.DOWN:
        return this.paneColors.red;
      case ServiceStatus.IMPAIRED:
      case ServiceStatus.ON_BACKUP:
        return this.paneColors.yellow;
      case ServiceStatus.UP:
      default:
        return this.paneColors.green;
    }
  }

  private createPopupComponent(
    mapMarker: MapMarker,
    asTooltip = false,
  ): HTMLElement {
    const Implementation: Class<LeafletXContent> = asTooltip
      ? LeafletTooltipContentComponent
      : LeafletPopupContentComponent;
    const componentRef = createComponent(Implementation, {
      environmentInjector: this.envInjector$,
    });

    componentRef.setInput('mapMarker', mapMarker);
    this.appRef$.attachView(componentRef.hostView);
    return componentRef.location.nativeElement;
  }

  private navigateToServices(mapMarker: MapMarker): void {
    const serviceNames = mapMarker.services
      .map((service) => service.name)
      .filter((name, index, array) => array.indexOf(name) === index);

    this.router$.navigate(['/services'], {
      queryParams: {
        groupBy: 'Location',
        services: serviceNames.join(','),
      },
    });
  }
}
